import {
  BreakoutRadioButton,
  BreakoutRadioGroup,
} from 'components/design-system/BreakoutRadioButton'
import { useMemo, useCallback, useId } from 'react'
import { WarningBox } from 'components/WarningBox'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { BreakoutAlert } from 'components/design-system/BreakoutAlert'
import { BreakoutPill } from 'components/design-system/BreakoutPill'
import { DateTime } from 'luxon'
import classNames from 'classnames'
import { useTranslationTyped } from 'i18n/i18n'
import { formatCentsToDollars } from '@breakoutlearning/firebase-repository/util'
import {
  FormLabel,
  HasNoAssociatedFormField,
} from 'components/design-system/form/FormLabel'

type ProductOption = {
  productId?: string
  productName?: string
  sectionPassPrice: number
  sectionPassQuantity: number
  isCustom: boolean
  /** When set, the option shows "Expires {date}", in red once it has passed. */
  expiresAt?: Date | null
}

export type PricingGroup = {
  key: string
  heading: string
  note?: string
  options: ProductOption[]
}

type LegacyProductOption = {
  sectionPassPrice: number
  sectionPassQuantity: number
  isCustom: true
  productName?: string
}

export function PricingSelectionWidget({
  customProducts,
  defaultProducts,
  legacyProductOption,
  selectedProduct,
  onSelectionChange,
  disabled = false,
  hasCorreOverride = false,
  groups,
  savedKey,
  showExpiry = false,
  hideLabel = false,
}: {
  customProducts: ProductOption[]
  defaultProducts: ProductOption[]
  legacyProductOption?: LegacyProductOption
  selectedProduct?: ProductOption
  onSelectionChange?: (selection: ProductOption) => void
  disabled?: boolean
  hasCorreOverride?: boolean
  /**
   * Render the options under headings instead of one sorted list. Each
   * group sorts by price then name on its own; customProducts and
   * defaultProducts are ignored when groups are given.
   */
  groups?: PricingGroup[]
  /** productId (or 'legacy' for Custom) of the saved option; shows a Saved badge. */
  savedKey?: string
  showExpiry?: boolean
  hideLabel?: boolean
}) {
  const { tt } = useTranslationTyped()
  const labelId = useId()

  const allOptions = useMemo(() => {
    const all = groups
      ? groups.flatMap((g) => g.options)
      : [
          ...customProducts,
          ...defaultProducts,
          ...(legacyProductOption ? [legacyProductOption] : []),
        ]
    if (groups) return all
    return all.sort(byPriceThenName)
  }, [customProducts, defaultProducts, groups, legacyProductOption])

  const handleValueChange = useCallback(
    (value: string) => {
      const option = allOptions.find(
        (opt) =>
          ('productId' in opt ? (opt.productId ?? 'legacy') : 'legacy') ===
          value
      )
      if (option) {
        onSelectionChange?.(option)
      }
    },
    [allOptions, onSelectionChange]
  )

  if (allOptions.length === 0) {
    return null
  }

  const renderProductOption = (option: ProductOption) => {
    const key = option.productId ?? 'legacy'
    const isSaved = savedKey !== undefined && savedKey === key
    const expiresAt = option.expiresAt
      ? DateTime.fromJSDate(option.expiresAt)
      : null
    const expired = expiresAt ? expiresAt < DateTime.now() : false
    if (option.isCustom && !option.productId) {
      return (
        <div className="grow" data-testid="pricing-option-custom" key="legacy">
          <BreakoutRadioButton
            value="legacy"
            label={
              <div className="flex flex-1 flex-row items-center gap-3">
                <div className="flex flex-1 flex-col gap-1">
                  <div className="text-title-medium text-on-surface">
                    {tt.instructor_class.billing_access.section_pass.custom()}
                  </div>
                  <div className="text-body-medium text-on-surface-var">
                    {tt.instructor_class.billing_access.section_pass.custom_hint()}
                  </div>
                </div>
                {isSaved && (
                  <BreakoutPill
                    kind="success"
                    label={tt.instructor_class.billing_access.saved()}
                  />
                )}
              </div>
            }
            kind="tertiary"
            variant="compact"
          />
        </div>
      )
    }
    const displayPrice = formatCentsToDollars(option.sectionPassPrice)

    const displayQuantity =
      option.sectionPassQuantity === -1
        ? tt.instructor_library.unlimited_assignments()
        : option.sectionPassQuantity === 1
          ? tt.instructor_library.max_of_1_assignment()
          : tt.instructor_library.max_of_assignments({
              count: option.sectionPassQuantity,
            })

    return (
      <BreakoutTooltip
        enabled={disabled}
        content={tt.instructor_library.section_pricing_warning()}
        key={option.productId ?? 'legacy'}
      >
        <div
          className="grow"
          data-testid={`pricing-option-${option.productId ?? 'legacy'}`}
        >
          <BreakoutRadioButton
            value={option.productId ?? 'legacy'}
            label={
              <div className="flex flex-1 flex-row items-center gap-3">
                <div className="flex flex-1 flex-col gap-1">
                  <div className="text-title-medium whitespace-nowrap text-on-surface">
                    {option.productName ? option.productName + ' - ' : ''}$
                    {displayPrice}
                    {tt.instructor_library.per_student()}
                  </div>
                  <div className="text-body-medium text-on-surface-var">
                    {displayQuantity}
                  </div>
                  {showExpiry && (
                    <div
                      className={classNames('text-body-medium', {
                        'text-core-error': expired,
                        'text-on-surface-var': !expired,
                      })}
                      data-testid="pricing-option-expiry"
                    >
                      {expiresAt
                        ? expired
                          ? tt.instructor_class.billing_access.section_pass.expired_on(
                              {
                                date: expiresAt.toLocaleString(
                                  DateTime.DATE_MED
                                ),
                              }
                            )
                          : tt.instructor_class.billing_access.section_pass.expires_on(
                              {
                                date: expiresAt.toLocaleString(
                                  DateTime.DATE_MED
                                ),
                              }
                            )
                        : tt.instructor_class.billing_access.section_pass.never()}
                    </div>
                  )}
                </div>
                {isSaved && (
                  <BreakoutPill
                    kind="success"
                    label={tt.instructor_class.billing_access.saved()}
                  />
                )}
              </div>
            }
            kind="tertiary"
            variant="compact"
          />
        </div>
      </BreakoutTooltip>
    )
  }

  return (
    <div className="flex flex-col gap-2" data-testid="pricing-field">
      {!hideLabel && (
        <FormLabel
          id={labelId}
          label={tt.instructor_library.pricing()}
          required
          for={HasNoAssociatedFormField}
        />
      )}

      {!disabled && hasCorreOverride && (
        <WarningBox className="mt-4">
          <div className="text-body-medium">
            {tt.instructor_library.pricing_override_detected()}
          </div>
          <div className="text-body-small">
            {tt.instructor_library.pricing_override_detected_description()}
          </div>
        </WarningBox>
      )}

      <BreakoutRadioGroup
        required
        value={
          // With groups (the Billing & Access tab) nothing selected means
          // nothing checked; Custom is an explicit choice there.
          groups && !selectedProduct
            ? ''
            : (selectedProduct?.productId ?? 'legacy')
        }
        onValueChange={handleValueChange}
        disabled={disabled}
        className="flex flex-col gap-2"
        aria-labelledby={labelId}
      >
        {groups
          ? groups.map((group) => (
              <div key={group.key} className="flex flex-col gap-2">
                <div className="mt-2">
                  <div className="text-label-large font-[600] text-on-surface">
                    {group.heading}
                  </div>
                  {group.note && (
                    <div className="text-body-medium text-on-surface-var">
                      {group.note}
                    </div>
                  )}
                </div>
                {[...group.options]
                  .sort(byPriceThenName)
                  .map(renderProductOption)}
              </div>
            ))
          : allOptions.map(renderProductOption)}
      </BreakoutRadioGroup>

      {selectedProduct?.isCustom && (
        <div>
          <div className="max-h-2 grow" />
          <BreakoutAlert variant="info">
            {tt.instructor_class.custom_pricing_notice()}
          </BreakoutAlert>
        </div>
      )}
    </div>
  )
}

function byPriceThenName(a: ProductOption, b: ProductOption) {
  // Custom (no productId) always sorts last within its group.
  if (!a.productId !== !b.productId) return a.productId ? -1 : 1
  if (a.sectionPassPrice !== b.sectionPassPrice) {
    return a.sectionPassPrice - b.sectionPassPrice
  }
  return (a.productName ?? '').localeCompare(b.productName ?? '')
}
